import React, { useState, useEffect, ChangeEvent, useRef } from 'react'; 
import { useParams, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'; 
import { useMsal } from "@azure/msal-react";
import { getTicket, TicketDetail, updateTicket, reprocessDocument } from '../services/apiService';
import { pushTicketsBatch, isDateInValidPeriod, getTrainingFolders, addToTraining } from '../services/ticketApiService';
import { useUser } from '../contexts/UserContext';
import { useFinancialPeriods } from '../hooks/useFinancialPeriods';
import { getTicketMetadata, TicketMetadata } from '../services/scansApiService';
import { Save, Send, ScanEye, ChevronLeft, ChevronRight, ChevronFirst, ChevronLast, AlertCircle, CheckCircle2, Layers, FolderPlus } from 'lucide-react';
import { ProDatePicker } from '../components/ProDatePicker';
import { CustomSelect } from '../components/CustomSelect';

function DetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { instance, accounts } = useMsal();

    // Queue navigation state — try sessionStorage (new tab), fall back to location.state (legacy)
    const queueKey = searchParams.get('queueKey');
    const queueState = queueKey
        ? JSON.parse(sessionStorage.getItem(queueKey) || 'null')
        : location.state;
    const { queue, index, returnPath, batchId } = queueState || {};

    const [queueInput, setQueueInput] = useState<string | null>(null);
    const [ticket, setTicket] = useState<TicketDetail | null>(null);
    const [editedTicket, setEditedTicket] = useState<Partial<TicketDetail>>({}); 
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [isPushing, setIsPushing] = useState<boolean>(false); 
    const [pushError, setPushError] = useState<string | null>(null);
    const [isReprocessing, setIsReprocessing] = useState<boolean>(false);
    const [reprocessError, setReprocessError] = useState<string | null>(null);
    const [metadata, setMetadata] = useState<TicketMetadata | null>(null);
    const { isAdmin, entityConfig } = useUser();
    const batchRoute = entityConfig?.useBatchPageV2 ? "/batch-v2" : "/batch";
    const [trainingFolders, setTrainingFolders] = useState<string[]>([]);
    const [showTrainingPicker, setShowTrainingPicker] = useState(false);
    const [isAddingToTraining, setIsAddingToTraining] = useState(false);
    const [trainingMsg, setTrainingMsg] = useState<string | null>(null);
    const trainingRef = useRef<HTMLDivElement>(null);

    const financialPeriods = useFinancialPeriods();
    const uomOptions = ["TN", "GA", "EA"].map((u) => ({ label: u, value: u }));

    const currentPeriod = financialPeriods.length > 0 ? financialPeriods[0] : null;
    const orderDateOnly = editedTicket?.orderDate ? editedTicket.orderDate.substring(0, 10) : '';
    const orderDateInCurrent = !!(currentPeriod && orderDateOnly &&
        orderDateOnly >= currentPeriod.startDate.substring(0, 10) &&
        orderDateOnly <= currentPeriod.endDate.substring(0, 10));
    const postPeriodMatchesCurrent = !!(currentPeriod && editedTicket?.postPeriod &&
        editedTicket.postPeriod === currentPeriod.periodId);

    const orderDateIssue = !editedTicket?.orderDate
        ? "Order Date is required."
        : !currentPeriod
            ? ""
            : !orderDateInCurrent
                ? `Order Date must be within the current open period (${currentPeriod.periodId}: ${currentPeriod.startDate.substring(0,10)} – ${currentPeriod.endDate.substring(0,10)}).`
                : "";

    const postPeriodIssue = !editedTicket?.postPeriod
        ? "Post Period is required."
        : !currentPeriod
            ? ""
            : !postPeriodMatchesCurrent
                ? `Post Period must match the current open period (${currentPeriod.periodId}).`
                : "";

    const fetchAll = async () => {
        if (!id || !accounts[0]) return;
        try {
            setIsLoading(true);
            const [ticketData, metaData] = await Promise.all([
                getTicket(instance, accounts[0], id),
                getTicketMetadata()
            ]);
            setTicket(ticketData);
            setEditedTicket(ticketData);
            setMetadata(metaData);
        } catch (err) {
            setError("Failed to load page data.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, [id, instance, accounts]);

    // Training folder picker: fetch folders on open, close on click outside
    useEffect(() => {
        if (!showTrainingPicker) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (trainingRef.current && !trainingRef.current.contains(e.target as Node)) {
                setShowTrainingPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTrainingPicker]);

    const handleTrainingClick = async () => {
        setTrainingMsg(null);
        if (trainingFolders.length === 0) {
            try {
                const folders = await getTrainingFolders(instance, accounts[0]);
                setTrainingFolders(folders);
            } catch {
                setTrainingMsg("Failed to load training folders.");
                return;
            }
        }
        setShowTrainingPicker(prev => !prev);
    };

    const handleSelectTrainingFolder = async (folder: string) => {
        if (!ticket) return;
        setIsAddingToTraining(true);
        setTrainingMsg(null);
        try {
            await addToTraining(instance, accounts[0], ticket.id, folder);
            setTrainingMsg(`Added to ${folder}`);
            setShowTrainingPicker(false);
        } catch {
            setTrainingMsg("Failed to add to training folder.");
        } finally {
            setIsAddingToTraining(false);
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setEditedTicket(prev => ({ ...prev, [name]: value }));
        setSaveSuccess(null); 
    };

    const handleSaveChanges = async () => {
        if (!id || !editedTicket) return;
        if (editedTicket.status === 'Pushed') {setError("You cannot edit a pushed ticket."); return;}

        setIsSaving(true);
        setError(null);
        setSaveSuccess(null);

        try {
            const changesToSend: { [key: string]: string } = {};
            for (const key in editedTicket) {
                if (ticket && editedTicket.hasOwnProperty(key) && ticket[key as keyof TicketDetail] !== editedTicket[key as keyof TicketDetail]) {
                    const value = editedTicket[key as keyof TicketDetail];
                    changesToSend[key] = value === null || value === undefined ? '' : String(value);
                }
            }

             if (Object.keys(changesToSend).length === 0) {
                setSaveSuccess("No changes detected.");
                setIsSaving(false);
                return;
            }

            await updateTicket(instance, accounts[0], id, changesToSend);
            setSaveSuccess('Changes saved successfully!');

            // Auto-advance to the next ticket in the queue after saving
            if (queue && index !== undefined && index < queue.length - 1) {
                navigateInQueue(index + 1);
                return;
            }

            fetchAll();

        } catch (err) {
            setError('Failed to save changes.');
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePushClick = async () => {
        if (!id) return;

        setIsPushing(true);
        setError(null);
        setPushError(null); 
        setSaveSuccess(null);

        if (JSON.stringify(ticket) !== JSON.stringify(editedTicket)) {
            alert("Please save changes before pushing.");
            setIsPushing(false);
            return;
        }

        try {
            const result = await pushTicketsBatch(instance, accounts[0], [Number(id)]);
            const ticketResult = result.results?.[0];
            if (ticketResult?.success) {
                alert('Successfully pushed to Acumatica.');
                navigate('/pending');
            } else {
                setPushError(ticketResult?.error || 'Failed to push to Acumatica.');
            }
        } catch (err: any) {
            setPushError(err.message || 'Failed to push to Acumatica.');
            console.error(err);
        } finally {
            setIsPushing(false);
        }
    };

    const handleReprocessClick = async () => {
        if (!id) return;

        setIsReprocessing(true);
        setError(null); 
        setReprocessError(null); 
        setSaveSuccess(null); 

        try {
            const result = await reprocessDocument(instance, accounts[0], id);
            alert(result.message); 
            const data = await getTicket(instance, accounts[0], id);
            setTicket(data);
            setEditedTicket(data);
        } catch (err: any) {
            setReprocessError(err.message || 'Failed to reprocess document.');
            console.error(err);
        } finally {
            setIsReprocessing(false);
        }
    };

    const confirmUnsaved = () => {
        if (JSON.stringify(ticket) !== JSON.stringify(editedTicket)) {
            return window.confirm("You have unsaved changes. Navigate away?");
        }
        return true;
    };

    const navigateInQueue = (newIndex: number) => {
        const state = { queue, index: newIndex, returnPath, batchId };
        if (queueKey) {
            sessionStorage.setItem(queueKey, JSON.stringify(state));
            navigate(`/ticket/${queue[newIndex]}?queueKey=${queueKey}`);
        } else {
            navigate(`/ticket/${queue[newIndex]}`, { state });
        }
    };

    const closeQueue = () => {
        if (queueKey) sessionStorage.removeItem(queueKey);
        navigate(returnPath || '/pending', {
            state: { selectedIds: queue }
        });
    };

    const handleFirst = () => {
        if (queue && index !== undefined && index > 0) {
            if (!confirmUnsaved()) return;
            navigateInQueue(0);
        }
    };

    const handlePrev = () => {
        if (queue && index !== undefined && index > 0) {
            if (!confirmUnsaved()) return;
            navigateInQueue(index - 1);
        }
    };

    const handleNext = () => {
        if (queue && index !== undefined && index < queue.length - 1) {
            if (!confirmUnsaved()) return;
            navigateInQueue(index + 1);
        }
    };

    const handleLast = () => {
        if (queue && index !== undefined && index < queue.length - 1) {
            if (!confirmUnsaved()) return;
            navigateInQueue(queue.length - 1);
        }
    };

    const handleReturnToBatch = () => {
        if (!confirmUnsaved()) return;
        if (queueKey) sessionStorage.removeItem(queueKey);
        navigate(batchRoute, { state: { selectedBatchId: batchId } });
    };

    if (isLoading) return <div className="flex items-center justify-center h-full text-slate-500">Loading ticket details...</div>;
    const displayError = error || pushError || reprocessError;
    if (displayError && !ticket) return <div className="p-8 text-center text-rose-500">Error: {displayError}</div>;
    if (!ticket || !editedTicket) return <div className="p-8 text-center text-slate-500">Ticket not found.</div>;

    const hasChanges = JSON.stringify(ticket) !== JSON.stringify(editedTicket);

    const inputClass = "w-full rounded-lg border border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-800 text-slate-900 dark:text-white focus:border-primary focus:ring-primary text-sm py-1.5 px-2.5 transition-all";
    const blankHighlight = "!border-red-400 !bg-red-50 dark:!border-red-500/50 dark:!bg-red-500/10";
    const labelClass = "text-slate-700 dark:text-slate-300 text-xs font-bold uppercase tracking-wide mb-0.5 block";

    return (
        <div className="flex h-full overflow-hidden bg-white dark:bg-background-dark">
            {/* Left Sidebar: Data Entry Form */}
            <aside className="w-[400px] flex flex-col bg-white dark:bg-surface-dark border-r border-slate-200 dark:border-slate-800 shrink-0 z-10 shadow-xl shadow-slate-200/50 dark:shadow-none">
                {/* Breadcrumbs & Header */}
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-surface-dark">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1">
                            <button
                                onClick={closeQueue}
                                title={returnPath ? `Back to ${returnPath.replace('/', '')}` : 'Back'}
                                className="p-1 -ml-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <h1 className="text-slate-900 dark:text-white text-lg font-bold tracking-tight">Details</h1>
                        </div>

                        <div className="flex items-center gap-1.5">
                            {/* Status Badges */}
                            <div className="flex items-center gap-1">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                    ticket.status === 'Pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' :
                                    ticket.status === 'Pushed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' :
                                    ticket.status === 'Deleted' ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400' :
                                    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                                }`}>
                                    {ticket.status}
                                </span>
                                {ticket.duplicatedTicketNumber && <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400 text-[10px] font-bold uppercase tracking-wider">Dup</span>}
                            </div>

                            {/* Queue Navigation */}
                            {queue && (
                                <>
                                    <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                                        <button onClick={handleFirst} disabled={index <= 0} className="p-1 rounded-md text-slate-500 hover:text-primary hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-all" title="First">
                                            <ChevronFirst size={20} />
                                        </button>
                                        <button onClick={handlePrev} disabled={index <= 0} className="p-1 rounded-md text-slate-500 hover:text-primary hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-all" title="Previous">
                                            <ChevronLeft size={20} />
                                        </button>
                                        <span className="text-sm font-bold text-slate-400 px-0.5 whitespace-nowrap flex items-center">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                value={queueInput ?? String(index + 1)}
                                                onChange={(e) => setQueueInput(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                                onBlur={() => {
                                                    const n = parseInt(queueInput ?? '', 10);
                                                    if (!isNaN(n) && n >= 1 && n <= queue.length && n - 1 !== index) {
                                                        if (confirmUnsaved()) navigateInQueue(n - 1);
                                                    }
                                                    setQueueInput(null);
                                                }}
                                                onFocus={(e) => { setQueueInput(String(index + 1)); e.target.select(); }}
                                                className="w-7 text-center bg-transparent outline-none focus:bg-white dark:focus:bg-slate-700 focus:text-slate-900 dark:focus:text-white rounded text-sm font-bold"
                                            />
                                            /{queue.length}
                                        </span>
                                        <button onClick={handleNext} disabled={index >= queue.length - 1} className="p-1 rounded-md text-slate-500 hover:text-primary hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-all" title="Next">
                                            <ChevronRight size={20} />
                                        </button>
                                        <button onClick={handleLast} disabled={index >= queue.length - 1} className="p-1 rounded-md text-slate-500 hover:text-primary hover:bg-white dark:hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-transparent transition-all" title="Last">
                                            <ChevronLast size={20} />
                                        </button>
                                    </div>
                                    {batchId && (
                                        <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                                            <button
                                                onClick={handleReturnToBatch}
                                                className="p-1 rounded-md text-slate-500 hover:text-primary hover:bg-white dark:hover:bg-slate-700 transition-all"
                                                title="Return to Batch"
                                            >
                                                <Layers size={20} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Form Content (Scrollable) */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                    
                    {/* Notifications */}
                    {(saveSuccess || displayError) && (
                        <div className={`p-3 rounded-xl text-sm font-medium flex items-start gap-2 ${saveSuccess ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400'}`}>
                            {saveSuccess ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
                            <p>{saveSuccess || displayError}</p>
                        </div>
                    )}

                    {/* Group 1: General Info */}
                    <div className="space-y-2">
                        <h3 className="text-slate-400 text-[11px] font-bold uppercase tracking-widest border-b border-slate-100 dark:border-slate-800 pb-1">General Information</h3>
                        
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className={labelClass}>Ticket Number</label>
                                <input type="text" name="ticketNumber" value={editedTicket.ticketNumber || ''} onChange={handleInputChange} className={`${inputClass} ${!editedTicket.ticketNumber ? blankHighlight : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Order Date</label>
                                <ProDatePicker
                                    value={editedTicket.orderDate ? editedTicket.orderDate.substring(0, 10) : ''}
                                    onChange={(val) => setEditedTicket(prev => ({ ...prev, orderDate: val }))}
                                    className={`${inputClass} ${orderDateIssue ? blankHighlight : ''}`}
                                    title={orderDateIssue || undefined}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className={labelClass}>Branch</label>
                                <input type="text" name="branch" value={editedTicket.branch || ''} onChange={handleInputChange} className={`${inputClass} ${!editedTicket.branch ? blankHighlight : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>Plant</label>
                                <input type="text" name="warehouse" value={editedTicket.warehouse || ''} onChange={handleInputChange} className={`${inputClass} ${!editedTicket.warehouse ? blankHighlight : ''}`} />
                            </div>
                        </div>
                        
                        <div>
                            <label className={labelClass}>Quarry</label>
                            <CustomSelect
                                placeholder='Location'
                                value={editedTicket.siteid || ''}
                                options={metadata?.locations.map(v => ({
                                    label: v,
                                    value: v
                                })) || []}
                                onChange={(val) => setEditedTicket(prev => ({ ...prev, siteid: val }))}
                                triggerClassName={!editedTicket.siteid ? blankHighlight : ''}
                            />
                        </div>
                    </div>

                    {/* Group 2: Item Details */}
                    <div className="space-y-2">
                        <h3 className="text-slate-400 text-[11px] font-bold uppercase tracking-widest border-b border-slate-100 dark:border-slate-800 pb-1">Item Details</h3>
                        
                        <div>
                            <label className={labelClass}>Supplier</label>
                            <CustomSelect
                                placeholder='Vendor'
                                value={editedTicket.vendorID || ''}
                                options={metadata?.vendors.map(v => ({
                                    label: `${v.vendorId} - ${v.vendorIddesc}`,
                                    value: v.vendorId
                                })) || []}
                                onChange={(val) => setEditedTicket(prev => ({ ...prev, vendorID: val }))}
                                triggerClassName={!editedTicket.vendorID ? blankHighlight : ''}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className={labelClass}>Inventory ID</label>
                                <CustomSelect
                                placeholder='Inventory'
                                value={editedTicket.inventoryID || ''}
                                options={metadata?.coverSheetInventoryItems.map(v => ({
                                    label: `${v.inventoryId} - ${v.inventoryDesc}`,
                                    value: v.inventoryId
                                })) || []}
                                onChange={(val) => setEditedTicket(prev => ({ ...prev, inventoryID: val }))}
                                triggerClassName={!editedTicket.inventoryID ? blankHighlight : ''}
                            />
                            </div>
                            <div>
                                <label className={labelClass}>UOM</label>
                                <CustomSelect
                                placeholder='UOM'
                                value={editedTicket.uom || ''}
                                options={uomOptions}
                                onChange={(val) => setEditedTicket(prev => ({ ...prev, uom: val }))}
                                triggerClassName={!editedTicket.uom ? blankHighlight : ''}
                                />
                                {/* <input type="text" name="uom" value={editedTicket.uom || ''} onChange={handleInputChange} className={inputClass} /> */}
                            </div>
                        </div>

                        <div>
                            <label className={labelClass}>Description</label>
                            <input type="text" name="description" value={editedTicket.description || ''} onChange={handleInputChange} className={inputClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Received Quantity</label>
                            <div className="relative">
                                <input type="number" step="0.01" name="receivedQty" value={editedTicket.receivedQty ?? ''} onChange={handleInputChange} className={`${inputClass} font-bold text-primary ${!editedTicket.receivedQty ? blankHighlight : ''}`} />
                            </div>
                        </div>
                    </div>

                    {/* Group 3: Logistics */}
                    <div className="space-y-2">
                        <h3 className="text-slate-400 text-[11px] font-bold uppercase tracking-widest border-b border-slate-100 dark:border-slate-800 pb-1">Logistics</h3>
                        
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className={labelClass}>Truck</label>
                                <input type="text" name="truckID" value={editedTicket.truckID || ''} onChange={handleInputChange} className={`${inputClass} ${!editedTicket.truckID && editedTicket.branch!=='CRM' ? blankHighlight : ''}`} />
                            </div>
                            <div>
                            <label className={labelClass}>Hauler</label>
                            <CustomSelect
                                placeholder='Freight Vendor'
                                value={editedTicket.freightVendorID || ''}
                                options={metadata?.freightVendors.map(v => ({
                                    label: `${v.freightVendorId} - ${v.freightVendorIddesc}`,
                                    value: v.freightVendorId
                                })) || []}
                                onChange={(val) => setEditedTicket(prev => ({ ...prev, freightVendorID: val }))}
                                triggerClassName={!editedTicket.freightVendorID ? blankHighlight : ''}
                            />
                        </div>
                        </div>
                    </div>

                    {/* Group 4: Accounting */}
                    <div className="space-y-2">
                        <h3 className="text-slate-400 text-[11px] font-bold uppercase tracking-widest border-b border-slate-100 dark:border-slate-800 pb-1">Accounting</h3>
                        
                        <div>
                            <label className={labelClass}>Blanket PO</label>
                            <input type="text" name="blanketPurchaseOrder" value={editedTicket.blanketPurchaseOrder || ''} onChange={handleInputChange} className={`${inputClass} ${!editedTicket.blanketPurchaseOrder ? blankHighlight : ''}`} />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className={labelClass}>Post Period</label>
                                <input type="text" name="postPeriod" value={editedTicket.postPeriod || ''} onChange={handleInputChange} title={postPeriodIssue || undefined} className={`${inputClass} ${postPeriodIssue ? blankHighlight : ''}`} />
                            </div>
                            <div>
                                <label className={labelClass}>CGO Project</label>
                                <input type="text" name="cgoProjectID" value={editedTicket.cgoProjectID || ''} onChange={handleInputChange} className={inputClass} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="px-4 py-2.5 border-t border-slate-200 dark:border-slate-800 flex gap-3 bg-slate-50/50 dark:bg-slate-900/20 backdrop-blur-sm">
                    <button
                        onClick={handleSaveChanges}
                        disabled={isSaving || !editedTicket || !hasChanges}
                        className={`flex-1 flex items-center justify-center gap-2 font-bold py-2.5 px-4 rounded-xl transition-all active:scale-95 disabled:active:scale-100 ${hasChanges ? 'bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20' : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'}`}
                    >
                        <Save size={18} />
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    
                    <button 
                        onClick={handlePushClick}
                        disabled={isSaving || isPushing || ticket.status !== 'Pending'}
                        className="flex items-center justify-center w-12 h-12 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 hover:border-emerald-200 dark:hover:border-emerald-500/20 rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                        title="Push to Acumatica"
                    >
                        {isPushing ? <span className="animate-spin text-xl">⟳</span> : <Send size={20} />}
                    </button>

                    <button
                        onClick={handleReprocessClick}
                        disabled={isSaving || isReprocessing || ticket.status !== 'Pending'}
                        className="flex items-center justify-center w-12 h-12 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-primary hover:bg-primary/5 dark:hover:bg-primary/10 hover:border-primary/30 rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                        title="Rerun Scan"
                    >
                        {isReprocessing ? <span className="animate-spin text-xl">⟳</span> : <ScanEye size={20} />}
                    </button>

                    {isAdmin && (
                        <div className="relative" ref={trainingRef}>
                            <button
                                onClick={handleTrainingClick}
                                disabled={isAddingToTraining}
                                className="flex items-center justify-center w-12 h-12 border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10 hover:border-amber-200 dark:hover:border-amber-500/20 rounded-xl transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                                title="Add to Training Data"
                            >
                                {isAddingToTraining ? <span className="animate-spin text-xl">⟳</span> : <FolderPlus size={20} />}
                            </button>

                            {showTrainingPicker && (
                                <div className="absolute bottom-full mb-2 right-0 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                                    <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 dark:border-slate-700">
                                        Select Training Folder
                                    </div>
                                    <div className="max-h-48 overflow-y-auto">
                                        {trainingFolders.map(folder => (
                                            <button
                                                key={folder}
                                                onClick={() => handleSelectTrainingFolder(folder)}
                                                className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                {folder}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {trainingMsg && (
                                <div className="absolute bottom-full mb-2 right-14 whitespace-nowrap px-3 py-1.5 text-xs font-medium bg-slate-800 text-white rounded-lg shadow-lg">
                                    {trainingMsg}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </aside>

            {/* Right Side: PDF Viewer */}
            <section className="flex-1 flex flex-col bg-slate-100 dark:bg-[#0b0f14] relative overflow-hidden">
                {ticket.pdfViewUrl ? (
                    <object
                        data={ticket.pdfViewUrl}
                        type="application/pdf" 
                        className="w-full h-full"
                        aria-label={`PDF Viewer for Ticket ${ticket.id}`} 
                    >
                        <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-4">
                            <p className="text-lg font-medium">Unable to display PDF inline.</p>
                            <a 
                                href={ticket.pdfViewUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors"
                            >
                                Download PDF
                            </a>
                        </div>
                    </object>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
                        <div className="w-12 h-12 border-4 border-slate-200 border-t-primary rounded-full animate-spin"></div>
                        <p className="font-medium">Loading Document...</p>
                    </div>
                )}
            </section>
        </div>
    );
}

export default DetailPage;